/**
 * La ficha de una generacion: elegir variante, memoria y color.
 *
 * TRES PREGUNTAS, EN ESTE ORDEN
 * -----------------------------
 * El cliente llega buscando "el 18", no "el 18 Pro Max de 512 en negro". La
 * variante, la memoria y el color son las tres preguntas que se contestan
 * despues, y el orden es el mismo que usa Apple en su tienda: primero cual de
 * los tres telefonos, despues cuanta memoria, despues de que color.
 *
 * DOS DE ESOS SELECTORES ELIGEN UN PRODUCTO Y UNO NO
 * --------------------------------------------------
 * La VARIANTE y la MEMORIA eligen un producto: otro precio, otro stock, otro
 * codigo. El COLOR es una preferencia, porque un sellado se pide y se pide en
 * el color que el cliente quiera (lo dice `catalogo/colores.ts`, que es de
 * donde sale la tabla), y viaja en el mensaje. Con una salvedad: si esa memoria
 * tiene una fila cargada con ese color (0129), se manda esa, con su codigo
 * (`productoDelColor`), buscada en todo el catalogo y no solo en la tarjeta.
 *
 * Por eso las dos primeras arrancan elegidas —siempre hay una— y el color
 * arranca sin elegir: no hay un color por defecto que no sea elegir por el
 * cliente.
 */
import { useEffect, useRef, useState } from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { formatear } from "@tora/shared";

import { colorConTraduccion, colorDeIphone, coloresDelModelo } from "@/components/catalogo/colores";
import { cn } from "@/lib/utils";

import { useFotosDeModelo } from "./consultas";
import { Foto, IconoWhatsapp, Precio } from "./piezas";
import {
  capacidadDe,
  colorEnElLocal,
  datosDeUsado,
  estadoDeDisponibilidad,
  etiquetaDeMemoria,
  filasDelModelo,
  fotoDeColor,
  productoDelColor,
  type GrupoGeneracion,
  type ProductoPublico,
  precioEnDolares,
} from "./logica";

export interface FichaModeloProps {
  grupo: GrupoGeneracion | null;
  /**
   * Todo lo publicado. El color se resuelve contra esto y no contra la
   * tarjeta: la de «En stock» tiene solo las filas del local (`filasDelModelo`).
   */
  catalogo?: readonly ProductoPublico[];
  onCerrar: () => void;
  onAgregar: (p: ProductoPublico, color: string | null) => void;
}

export function FichaModelo({
  grupo,
  catalogo = [],
  onCerrar,
  onAgregar,
}: FichaModeloProps): JSX.Element | null {
  const [claveVariante, setClaveVariante] = useState<string | null>(null);
  const [idElegido, setIdElegido] = useState<number | null>(null);
  const [colorElegido, setColor] = useState<string | null>(null);
  // Que colores tienen foto. Se sabe de antemano para no pedir una imagen que
  // no existe y comerse un 404 por cada color que el cliente toca.
  const { data: fotos } = useFotosDeModelo();

  // Al cerrar, el foco vuelve a la tarjeta que la abrio: quedaba en el
  // <body> y quien usa el teclado arrancaba de nuevo desde el menu.
  const desde = useRef<HTMLElement | null>(null);
  useEffect(() => {
    if (grupo) {
      if (document.activeElement instanceof HTMLElement) desde.current = document.activeElement;
      return;
    }
    const volver = desde.current;
    desde.current = null;
    if (volver?.isConnected) requestAnimationFrame(() => volver.focus());
  }, [grupo]);

  // Abrir otra generacion empieza de cero. Arranca en la variante mas barata,
  // que es la que el cliente esta mirando en la tarjeta.
  useEffect(() => {
    const masBarata = grupo?.variantes.reduce((m, v) => (v.desde < m.desde ? v : m));
    setClaveVariante(masBarata?.clave ?? null);
    setIdElegido(masBarata?.capacidades[0]?.id ?? null);
    setColor(null);
  }, [grupo]);

  if (!grupo) return null;

  const variante = grupo.variantes.find((v) => v.clave === claveVariante) ?? grupo.variantes[0]!;
  const elegido = variante.capacidades.find((v) => v.id === idElegido) ?? variante.capacidades[0]!;
  // Los colores son los del MODELO, no los de la generacion: el 17 Pro y el 17
  // no vienen en los mismos.
  //
  // Y solo en un SELLADO. Un usado viene del color que vino: ofrecer la paleta
  // de Apple seria dejar elegir un color que puede no existir en el que se
  // consiga, que es justo la consulta que el catalogo evita.
  const etiquetaDe = (c: string): string => colorDeIphone(c)?.etiqueta ?? c;
  const filas = filasDelModelo(elegido, [...variante.todas, ...catalogo]);
  // Un color que no tiene fila propia ni fila sin color donde pedirse no se
  // ofrece: la unica fila seria la de otro color, y mandarla ataba esa unidad
  // a un cliente que pidio otra cosa.
  const colores = elegido.es_usado
    ? []
    : coloresDelModelo(elegido.nombre).filter(
        (c) => productoDelColor(elegido, filas, c.etiqueta, etiquetaDe) !== null,
      );
  // El color elegido puede no existir en la memoria a la que se paso: queda
  // como si no se hubiera elegido.
  const color = colores.some((c) => c.etiqueta === colorElegido) ? colorElegido : null;
  const hayVariantes = grupo.variantes.length > 1;
  const hayMemorias = variante.capacidades.length > 1 || capacidadDe(elegido) > 0;
  const datosUsado = elegido.es_usado ? datosDeUsado(elegido) : [];
  // Lo que se agrega: la fila de esa memoria en el color elegido, si la hay.
  // El precio y el "en stock" de arriba son los de ESA fila, que es la que va
  // en el mensaje.
  const aPedir = productoDelColor(elegido, filas, color, etiquetaDe) ?? elegido;
  const estado = estadoDeDisponibilidad([aPedir]);
  // Sin color elegido, si ese mismo equipo esta en el local en un color, se
  // dice cual: si no, habia que tocar los colores uno por uno para enterarse.
  const enLocalEn = color ? null : colorEnElLocal(elegido, filas);
  const aclaracion = enLocalEn
    ? `en el local en ${etiquetaDe(enLocalEn)}`
    : estado === "A PEDIDO"
      ? "lo traemos del proveedor"
      : estado === "SIN STOCK" && !elegido.es_usado
        ? "consultanos si lo conseguimos"
        : null;
  // Un renglon de la lista de usados no tiene ficha: todavia no hay aparato del
  // que medir la bateria. Aca si se dice, porque es UNA vez y es el momento en
  // que la persona esta decidiendo; en la lista eran diecinueve veces la misma
  // frase.
  const usadoSinFicha = elegido.es_usado && datosUsado.length === 0;
  // La foto del color elegido. Si ese color no tiene la suya, queda la del
  // producto: el equipo es el mismo y una foto de menos es mejor que un hueco.
  const fotoDelColor = fotoDeColor(fotos ?? [], elegido.nombre, color);

  /**
   * Cambiar de variante reinicia la memoria Y el color: ni una ni el otro
   * existen igual en el otro telefono. El 17 viene en Lavanda y el 17 Pro no,
   * asi que arrastrar la eleccion dejaria marcado un color que no se vende.
   */
  function elegirVariante(clave: string): void {
    const v = grupo!.variantes.find((x) => x.clave === clave);
    if (!v) return;
    setClaveVariante(clave);
    setIdElegido(v.capacidades[0]?.id ?? null);
    setColor(null);
  }

  // Para el lector de pantalla, lo que de verdad se elige: unos AirPods no
  // tienen ni memoria ni color.
  const opciones = [
    hayVariantes ? "el modelo" : null,
    hayMemorias ? "la memoria" : null,
    colores.length > 0 ? "el color" : null,
  ].filter((x): x is string => x !== null);
  const queElegir =
    opciones.length === 0
      ? "Agregalo a tu consulta para preguntarnos por WhatsApp."
      : `Elegí ${opciones.length === 1 ? opciones[0] : `${opciones.slice(0, -1).join(", ")} y ${opciones.at(-1)}`} antes de agregarlo a tu consulta.`;

  return (
    <DialogPrimitive.Root open onOpenChange={(v) => !v && onCerrar()}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-40 bg-black/50 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0" />
        <DialogPrimitive.Content
          className={cn(
            "fixed inset-x-0 bottom-0 z-50 flex max-h-[94dvh] flex-col rounded-t-[16px] bg-white text-[#0A0A0A]",
            "data-[state=open]:animate-in data-[state=closed]:animate-out",
            "data-[state=closed]:slide-out-to-bottom data-[state=open]:slide-in-from-bottom",
            // En la PC una hoja que sube desde abajo a todo el ancho deja la
            // foto en el medio de una franja blanca de dos metros: va centrada.
            // `inset-0` + `m-auto` + `h-fit` la centra sin `translate`, que se
            // pelearia con la animacion de entrada.
            //
            // Y en dos columnas, la foto a la izquierda y lo que se elige a la
            // derecha: una debajo de la otra no entran en una notebook (unos
            // 575 de alto) y el boton quedaba tapando los colores. La fila de
            // arriba es `minmax(0,1fr)` para que, si igual no entra, achique la
            // columna de las opciones con scroll y no el boton.
            "md:inset-0 md:m-auto md:h-fit md:max-h-[92dvh] md:rounded-[16px] md:shadow-[0_25px_50px_-12px_rgb(0_0_0/0.25)]",
            "md:grid md:w-[780px] md:max-w-[calc(100vw-3rem)] md:grid-cols-2 md:grid-rows-[minmax(0,1fr)_auto] md:overflow-hidden",
            "md:data-[state=closed]:slide-out-to-bottom-6 md:data-[state=open]:slide-in-from-bottom-6",
            "md:data-[state=closed]:fade-out-0 md:data-[state=open]:fade-in-0",
          )}
        >
          <div className="flex justify-end px-3 pt-3 md:absolute md:right-3 md:top-3 md:z-10 md:p-0">
            <DialogPrimitive.Close
              aria-label="Cerrar"
              className="flex size-9 items-center justify-center rounded-md text-[#5C5C5C]"
            >
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.75"
                strokeLinecap="round"
                aria-hidden="true"
                className="size-5"
              >
                <path d="M18 6 6 18M6 6l12 12" />
              </svg>
            </DialogPrimitive.Close>
          </div>

          {/* La foto de la PC, sola en su columna. En el celular va adentro de
              lo que se desliza (la de abajo), para que no se coma media hoja. */}
          <div className="hidden md:row-span-2 md:flex md:items-center md:justify-center md:p-8">
            <Foto producto={elegido} enVezDe={fotoDelColor} className="aspect-square w-full" />
          </div>

          <div className="min-h-0 flex-auto overflow-y-auto px-5 pb-2 text-center md:col-start-2 md:row-start-1 md:px-7 md:pb-0 md:pt-6 md:text-left">
            <Foto
              producto={elegido}
              enVezDe={fotoDelColor}
              className="mx-auto aspect-square w-[62%] md:hidden"
            />

            {/* USADO: un usado tambien puede llegar aca desde una seccion propia
                del home, y sin decirlo se leia como un sellado mas barato. Y
                donde esta lo que se va a pedir: en stock, a pedido o sin stock. */}
            <div className="mt-1 text-[11px] font-[700] tracking-[0.08em]">
              {[elegido.es_usado ? "USADO" : null, estado].filter(Boolean).join(" · ")}
              {/* Que quiere decir: A PEDIDO y SIN STOCK solos no le dicen al
                  cliente si vale la pena consultar. En el mismo renglon, para
                  no agrandar la ficha. */}
              {aclaracion ? (
                <span className="font-[400] tracking-normal text-[#6E6E6E]"> · {aclaracion}</span>
              ) : null}
            </div>

            <DialogPrimitive.Title className="mt-2 text-[26px] font-[700] tracking-[-0.03em] md:pr-10">
              {grupo.nombre}
              {hayVariantes && variante.etiqueta !== "Normal" ? ` ${variante.etiqueta}` : ""}
            </DialogPrimitive.Title>
            <DialogPrimitive.Description className="sr-only">
              {queElegir}
            </DialogPrimitive.Description>

            <div className="mt-1">
              <Precio
                pesos={formatear(Number(aPedir.precio_ars) || 0, "ARS")}
                dolares={precioEnDolares(aPedir)}
                className="text-[28px] md:text-[24px]"
                // En la PC, al lado: abajo era la linea que dejaba la aclaracion
                // de los colores afuera en una notebook.
                claseDolares="md:ml-3 md:inline-block md:whitespace-nowrap"
              />
            </div>

            {/* ── Variante: cual de los telefonos de la generacion ── */}
            {hayVariantes ? (
              <fieldset className="mt-7 border-0 p-0 text-left md:mt-4">
                <legend className="mb-2.5 text-[13px] font-semibold">Modelo</legend>
                <div className="flex flex-wrap gap-2">
                  {grupo.variantes.map((v) => {
                    const activo = v.clave === variante.clave;
                    return (
                      <button
                        key={v.clave}
                        type="button"
                        onClick={() => elegirVariante(v.clave)}
                        aria-pressed={activo}
                        className={cn(
                          "flex h-11 items-center justify-center rounded-md border px-4 text-sm font-semibold md:h-10",
                          activo
                            ? "border-[#0A0A0A] bg-[#0A0A0A] text-white"
                            : "border-[#E0E0E0] text-[#0A0A0A] hover:border-[#0A0A0A]",
                        )}
                      >
                        {v.etiqueta}
                      </button>
                    );
                  })}
                </div>
              </fieldset>
            ) : null}

            {/* ── Memoria: cada una es un producto distinto ── */}
            {hayMemorias ? (
              <fieldset className="mt-6 border-0 p-0 text-left md:mt-4">
                <legend className="mb-2.5 text-[13px] font-semibold">
                  {/* En un Mac la «memoria» es la RAM, y el nombre ya la dice
                      («16GB»): lo que se elige aca es el disco. */}
                  {/\b(mac|macbook|imac|ipad)\b/i.test(elegido.nombre)
                    ? "Almacenamiento"
                    : "Memoria"}
                </legend>
                <div className="flex flex-wrap gap-2">
                  {variante.capacidades.map((v) => {
                    const activo = v.id === elegido.id;
                    // "en stock" si lo que se mandaria con el color elegido
                    // esta en el local, no si lo esta la fila del boton.
                    // Sin color elegido, «en stock» si esa memoria esta en
                    // el local en algun color.
                    const enLocal =
                      (productoDelColor(v, filas, color, etiquetaDe) ?? v).en_local ||
                      (!color && colorEnElLocal(v, filas) !== null);
                    return (
                      <button
                        key={v.id}
                        type="button"
                        onClick={() => setIdElegido(v.id)}
                        aria-pressed={activo}
                        className={cn(
                          "flex h-11 min-w-[84px] flex-col items-center justify-center rounded-md border px-3.5 md:h-10",
                          activo
                            ? "border-[#0A0A0A] bg-[#0A0A0A] text-white"
                            : "border-[#E0E0E0] text-[#0A0A0A] hover:border-[#0A0A0A]",
                        )}
                      >
                        <span className="text-sm font-semibold tabular-nums">
                          {etiquetaDeMemoria(v, variante.capacidades)}
                        </span>
                        {enLocal ? (
                          <span
                            className={cn(
                              "text-[10px]",
                              activo ? "text-white/70" : "text-[#6E6E6E]",
                            )}
                          >
                            en stock
                          </span>
                        ) : null}
                      </button>
                    );
                  })}
                </div>
              </fieldset>
            ) : null}

            {/* ── Color: una preferencia, no un producto ── */}
            {colores.length > 0 ? (
              <fieldset className="mt-6 border-0 p-0 text-left md:mt-4">
                <legend className="mb-2.5 text-[13px] font-semibold">
                  Color{" "}
                  <span className="font-normal text-[#6E6E6E]">
                    {color ? `· ${colorConTraduccion(color)}` : "· el que quieras"}
                  </span>
                </legend>
                <div className="flex flex-wrap gap-2.5">
                  {colores.map((c) => {
                    const activo = c.etiqueta === color;
                    return (
                      <button
                        key={c.etiqueta}
                        type="button"
                        onClick={() => setColor(activo ? null : c.etiqueta)}
                        aria-pressed={activo}
                        aria-label={colorConTraduccion(c.etiqueta)}
                        title={colorConTraduccion(c.etiqueta)}
                        className={cn(
                          "size-11 rounded-full md:size-10",
                          activo
                            ? "ring-2 ring-[#0A0A0A] ring-offset-2"
                            : c.claro
                              ? "ring-1 ring-[#E0E0E0]"
                              : "",
                        )}
                        style={{ background: c.hex }}
                      />
                    );
                  })}
                </div>
                <p className="mt-2.5 text-[11px] leading-normal text-[#6E6E6E]">
                  Los sellados se piden en el color que elijas. Si no elegís, lo charlamos por
                  WhatsApp.
                </p>
              </fieldset>
            ) : null}

            {usadoSinFicha ? (
              <p className="mt-6 rounded-lg bg-[#F7F7F7] px-4 py-3.5 text-left text-[13px] leading-relaxed text-[#3A3A3A]">
                Te pasamos batería, color y estado de la unidad antes de que decidas. Va con
                garantía escrita.
              </p>
            ) : null}

            {/* ── La ficha de un usado ── */}
            {datosUsado.length > 0 ? (
              <div className="mt-6 text-left">
                {datosUsado.map((d) => (
                  <div
                    key={d.etiqueta}
                    className="flex justify-between border-b border-[#ECECEC] py-2.5 text-[13px]"
                  >
                    <span className="text-[#6E6E6E]">{d.etiqueta}</span>
                    <span className="font-semibold">{d.valor}</span>
                  </div>
                ))}
              </div>
            ) : null}
          </div>

          {/* `env(safe-area-inset-bottom)`: en el iPhone la barrita de inicio se
              come el borde de abajo y el boton queda cortado. */}
          <div className="border-t border-[#ECECEC] px-5 pb-[max(1.5rem,env(safe-area-inset-bottom))] pt-4 md:col-start-2 md:row-start-2 md:border-t-0 md:px-7 md:pb-6 md:pt-3">
            <button
              type="button"
              onClick={() => {
                onAgregar(aPedir, color);
                onCerrar();
              }}
              className="flex h-[54px] w-full items-center justify-center gap-2.5 rounded-lg bg-[#0A0A0A] text-[15px] font-[700] text-white md:h-12"
            >
              <IconoWhatsapp className="size-[19px]" />
              Agregar a mi consulta
            </button>
          </div>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}
